/**
 * Shared utility functions for chart generators
 */
import { getDeterministicColor } from '../../social-media/design-tokens'

// Re-export for social media code paths that still use it
export { getDeterministicColor };

/**
 * Tableau-inspired palette for Platinum table graphs.
 * 20 visually distinct, pleasant colors designed for data visualization.
 */
const PLATINUM_CHART_PALETTE = [
  '#4E79A7', // Steel blue
  '#F28E2B', // Warm orange
  '#E15759', // Soft red
  '#76B7B2', // Teal
  '#59A14F', // Green
  '#EDC948', // Gold
  '#B07AA1', // Mauve
  '#FF9DA7', // Salmon pink
  '#9C755F', // Tan
  '#BAB0AC', // Warm gray
  '#86BCB6', // Light teal
  '#8CD17D', // Light green
  '#F1CE63', // Light gold
  '#A0CBE8', // Light blue
  '#FFBE7D', // Light orange
  '#D4A6C8', // Light mauve
  '#FABFD2', // Light pink
  '#D7B5A6', // Light tan
  '#B6992D', // Dark gold
  '#499894', // Dark teal
];

/**
 * Legend and footer geometry, in PIXELS (#968 comment 4).
 *
 * The legend sits at the TOP of the chart, not the bottom, and is `plain`
 * rather than `scroll` (except the line chart, which pairs the top anchor
 * with `scroll` — see lineChartLegend). The placement is load-bearing and was
 * established by driving the real widget in a browser — the option object
 * alone tells you nothing here, because ECharts silently renders nothing
 * rather than reporting a layout it cannot satisfy:
 *
 *  - `type: 'scroll'` painted NOTHING at dashboard-cell sizes, with or
 *    without explicit `width`/`height` bounds. The legend was present and
 *    `show: true` in the merged option the whole time, which is why the
 *    invisible legend survived review: every assertion about it passed.
 *  - The bottom band is already owned by the dataZoom slider and the rotated
 *    date labels. A `plain` legend paints, but 11 long series names wrap to
 *    two rows that grow UPWARD from `bottom`, straight through the slider —
 *    ECharts does no collision avoidance between components, so nothing
 *    stops it.
 *
 * The top band has no such competition, fits two wrapped rows, and keeps the
 * native click-to-toggle behaviour. The stack is:
 *
 *   legend       top 4px, wrapping down to ~48px (two rows of 11 series)
 *   grid top edge at 56px
 *   ...plot...
 *   grid bottom edge at 56px  (axis labels included, `containLabel: true`)
 *   dataZoom     8px .. 24px
 *
 * Charts with no dataZoom slider use CHART_GRID_BOTTOM (24px).
 */
export const CHART_LEGEND_TOP = 4
/** Legend swatch height; with an 11px label this keeps each row compact. */
export const CHART_LEGEND_ITEM_HEIGHT = 10
/** Legend swatch width — narrower than the ECharts default to fit more series per row. */
export const CHART_LEGEND_ITEM_WIDTH = 18
/** Gap between legend entries. */
export const CHART_LEGEND_ITEM_GAP = 12
/** Legend width, leaving a small margin so wrapped rows are not flush to the edges. */
export const CHART_LEGEND_WIDTH = '96%'
/** `grid.top` for a chart carrying the legend: clears two wrapped legend rows. */
export const CHART_GRID_TOP_WITH_LEGEND = 56
export const CHART_DATAZOOM_BOTTOM = 8
export const CHART_DATAZOOM_HEIGHT = 16
/**
 * `grid.top` for a chart whose legend is HIDDEN (#1201).
 *
 * `hideLegend` only sets `legend.show: false`; the generator's
 * CHART_GRID_TOP_WITH_LEGEND (56px) stays put, so the plot kept paying for a
 * legend band that paints nothing — an empty stripe above every Sky emotion
 * and sentiment chart. PlatinumGraphs swaps in this value when it hides the
 * legend. 24px rather than 0 because the chart title, when a caller passes
 * one, paints in the same band (a 16px bold title is ~22px tall); the
 * dashboard path always passes an empty title, so there it is simply slack.
 */
export const CHART_GRID_TOP_NO_LEGEND = 24
/** `grid.bottom` for a chart with no dataZoom slider. */
export const CHART_GRID_BOTTOM = 24
/** `grid.bottom` for a chart with a dataZoom slider. */
export const CHART_GRID_BOTTOM_WITH_ZOOM = 56

/**
 * The legend every chart generator uses. Built here rather than repeated per
 * generator so the placement decision above has exactly one home — six copies
 * is how the previous arrangement drifted.
 *
 * `type` is deliberately absent, which means ECharts' default `'plain'`.
 *
 * On the historical "`'scroll'` renders nothing" finding (#968 comment 4,
 * re-diagnosed for #1184): the invisible scroll legend was the pre-#1068
 * BOTTOM-anchored config (`bottom: 10` under a dataZoom at `bottom: 50` and
 * rotated axis labels, grid bottom a shrinking percentage) — the single
 * scroll row painted into the clipped/contested footer band, so nothing of
 * it survived at dashboard-cell sizes while the option still reported
 * `show: true`. Re-tested in a standalone echarts 5.6.0 harness at 400x300
 * (including init-at-0-size-then-resize, the dashboard-cell mount path): a
 * TOP-anchored scroll legend paints correctly, one row with pager arrows.
 * So `'scroll'` is safe from this top anchor — lineChartLegend below uses
 * it — but any change here still needs a browser check, because no
 * option-object assertion can prove a legend actually paints.
 */
export function chartLegend(seriesNames: string[]) {
  return {
    data: seriesNames,
    top: CHART_LEGEND_TOP,
    left: 'center',
    width: CHART_LEGEND_WIDTH,
    itemHeight: CHART_LEGEND_ITEM_HEIGHT,
    itemWidth: CHART_LEGEND_ITEM_WIDTH,
    itemGap: CHART_LEGEND_ITEM_GAP,
    textStyle: { fontSize: 11 },
    selectedMode: 'multiple' as const,
  }
}

/**
 * `top` for the second legend row of a split line-chart legend (#1184
 * follow-up). Row 1 sits at CHART_LEGEND_TOP (4) and a scroll row's content
 * is ~15-18px tall, so 26 stacks row 2 directly beneath it; the row's
 * bottom edge lands around 51px, inside the CHART_GRID_TOP_WITH_LEGEND (56)
 * band — verified in a browser at 400x300, 350x220 and 700x350, along with
 * the no-overlap-with-the-plot requirement.
 */
export const CHART_LEGEND_ROW2_TOP = 26

/**
 * Series count at which the line chart's legend splits into two rows.
 * Below this, one paginated row is enough and two rows would look sparse;
 * from here up, the second row roughly doubles the visible series names.
 */
export const LINE_LEGEND_SPLIT_THRESHOLD = 5

/**
 * The line chart's legend (#1184): same top-anchored geometry as
 * chartLegend, plus scroll pagination and a hover tooltip.
 *
 * Matt's follow-up on #1184 asked for TWO legend rows — one paginated row
 * showed too few series. An ECharts horizontal scroll legend cannot wrap
 * (re-verified in echarts 5.6.0: `type: 'scroll'` with a `height` bound
 * still renders a single row), and a plain legend wraps but overflows the
 * top band into the plot at three-plus rows (11 series at 400px wrap to
 * three rows that paint straight through the grid — ECharts does no
 * collision avoidance). So with LINE_LEGEND_SPLIT_THRESHOLD or more series
 * this returns TWO stacked scroll legends, the series split between them:
 * two rows that each paginate independently, always inside the top band.
 * When everything fits, neither row shows a pager and the result looks like
 * a wrapped two-row legend. Below the threshold it is the single scroll row
 * (#1188 behaviour). Verified painting in a browser at 400x300, 350x220 and
 * 700x350 with 11 series (see the note on chartLegend — the historical
 * invisible-scroll failure was the bottom anchor, not `'scroll'` itself).
 *
 * `tooltip.show` makes hovering a legend item show its full label, which is
 * what makes truncated/paged labels readable. Line charts only for now —
 * bar/column/pie legend behaviour is issue #1172's scope.
 *
 * NOTE for consumers post-processing the option: `legend` here can be an
 * ARRAY. Spreading it as an object (`{ ...legend, show: false }`) silently
 * produces garbage — see the array handling in PlatinumGraphs' hideLegend
 * patch and applyChartDesign's mergeLegend.
 */
export function lineChartLegend(seriesNames: string[]) {
  const row = (names: string[]) => ({
    ...chartLegend(names),
    type: 'scroll' as const,
    tooltip: { show: true },
  })

  if (seriesNames.length < LINE_LEGEND_SPLIT_THRESHOLD) {
    return row(seriesNames)
  }

  const mid = Math.ceil(seriesNames.length / 2)
  return [
    row(seriesNames.slice(0, mid)),
    { ...row(seriesNames.slice(mid)), top: CHART_LEGEND_ROW2_TOP },
  ]
}

/**
 * Get a chart color by index, cycling through the Platinum palette.
 * Used for Platinum table graphs to ensure visually distinct, ordered colors.
 */
export function getChartColorByIndex(index: number): string {
  return PLATINUM_CHART_PALETTE[index % PLATINUM_CHART_PALETTE.length];
}

/**
 * Converts a string potentially containing a percentage or plain number into a numeric value.
 * Handles null, undefined, empty strings, and the '%' sign.
 *
 * @param str - The string value from a table cell.
 * @returns The numeric value (e.g., '50%' becomes 0.5), or null if parsing fails.
 */
export function toNumber(str: string | null | undefined): number | null {
  if (str === null || str === undefined || str.trim() === '') {
    return null
  }
  const isPercent = str.includes('%')
  const cleanedStr = str.replace('%', '').trim()
  const value = parseFloat(cleanedStr)

  if (isNaN(value)) {
    return null
  }

  return isPercent ? value / 100 : value
}

/**
 * Formatter function for ECharts axis labels to display numbers as percentages.
 *
 * @param value - The numeric value from the axis.
 * @returns A string formatted as a percentage (e.g., 0.75 becomes "75%").
 */
export function axisPercentFormatter(value: number): string {
  return `${(value * 100).toFixed(0)}%`
}

/**
 * Formatter function for ECharts tooltips to display numbers as percentages with one decimal place.
 *
 * @param value - The numeric value.
 * @returns A string formatted as a percentage (e.g., 0.756 becomes "75.6%") or 'N/A'.
 */
export function tooltipPercentFormatter(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A'
  }
  return `${(value * 100).toFixed(1)}%`
}

/**
 * The data-label option a column/bar series carries when a graph config opts
 * in via `showDataLabels` (#1201).
 *
 * OPT-IN, DEFAULT OFF, on purpose: this generator draws every clustered
 * column in the product, and Sky asking to read the emotion split off the
 * chart is not a reason to print numbers on top of everyone else's bars.
 *
 * `position: 'top'` puts the figure ABOVE the column, which is what the issue
 * asks for — ECharts' own default for a bar label is 'inside'. Percentages
 * print at 0dp to match the axis ticks beside them (axisPercentFormatter),
 * frequencies print as plain counts.
 *
 * Returns undefined when not opted in, so the caller can spread it away and
 * leave the series option byte-identical to before.
 */
export function columnDataLabel(
  settings?: { showDataLabels?: boolean; dataType?: string }
): { show: true; position: 'top'; fontSize: number; formatter: (params: { value?: unknown }) => string } | undefined {
  if (!settings?.showDataLabels) return undefined
  const isFreq = settings.dataType === 'freq'
  return {
    show: true,
    position: 'top',
    fontSize: 10,
    formatter: (params) => {
      const raw = params?.value
      const value = typeof raw === 'number' ? raw : null
      if (value === null || isNaN(value)) return ''
      return isFreq ? value.toLocaleString() : axisPercentFormatter(value)
    },
  }
}

/**
 * Default color palette for charts
 * Using ECharts' default color scheme
 */
export const DEFAULT_COLORS = [
  '#5470C6',
  '#91CC75',
  '#FAC858',
  '#EE6666',
  '#73C0DE',
  '#3BA272',
  '#FC8452',
  '#9A60B4',
  '#EA7CCC',
]

/**
 * Resolve colors for series based on colorMap or fallback to index-based palette colors
 * @param seriesNames - Array of series names to get colors for
 * @param colorMap - Optional map of series name to color
 * @returns Array of colors in series order
 */
export function resolveSeriesColors(
  seriesNames: string[],
  colorMap?: Record<string, string>
): string[] {
  if (!colorMap) {
    // Use index-based colors for visually distinct, ordered palette
    return seriesNames.map((_, i) => getChartColorByIndex(i));
  }

  return seriesNames.map((name, i) => {
    const key = name.toLowerCase().trim();
    // Use colorMap if available, otherwise fall back to index-based color
    return colorMap[key] || getChartColorByIndex(i);
  });
}

/**
 * Build a flat color map from a dashboard brand palette and optional per-widget overrides.
 * All keys are lowercased for case-insensitive matching against series names.
 * Returns undefined if no entries exist (preserves existing index-based fallback).
 */
export function buildColorMap(
  brandPalette?: Record<string, { color: string; aliases?: string[] }>,
  colorOverrides?: Record<string, string>,
): Record<string, string> | undefined {
  const map: Record<string, string> = {}
  let hasEntries = false

  if (brandPalette) {
    for (const [name, entry] of Object.entries(brandPalette)) {
      map[name.toLowerCase().trim()] = entry.color
      if (entry.aliases) {
        for (const alias of entry.aliases) {
          map[alias.toLowerCase().trim()] = entry.color
        }
      }
      hasEntries = true
    }
  }

  if (colorOverrides) {
    for (const [name, color] of Object.entries(colorOverrides)) {
      map[name.toLowerCase().trim()] = color
      hasEntries = true
    }
  }

  return hasEntries ? map : undefined
}

/**
 * Zero-width space used to tell repeated category names apart (#1808).
 */
const CATEGORY_DISAMBIGUATOR = '\u200B'

/**
 * Makes repeated category names distinct by appending zero-width spaces to
 * the 2nd, 3rd... occurrence (#1808). Two rows both labelled "Sky" must draw
 * as two bars; a category axis keyed by name would otherwise treat them as
 * one slot. The suffix is invisible, and `cleanCategoryName` strips it
 * wherever a name is shown or matched. Unique names come back unchanged, so
 * a chart without repeats is byte-identical to before.
 */
export function uniqueCategoryNames(names: string[]): string[] {
  const seen = new Map<string, number>()
  return names.map((name) => {
    const n = seen.get(name) ?? 0
    seen.set(name, n + 1)
    return n === 0 ? name : name + CATEGORY_DISAMBIGUATOR.repeat(n)
  })
}

/** Removes the zero-width suffix `uniqueCategoryNames` adds. */
export function cleanCategoryName(name: string): string {
  return typeof name === 'string' ? name.split(CATEGORY_DISAMBIGUATOR).join('') : name
}

/**
 * Custom error class for generator issues
 */
export class GeneratorError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GeneratorError'
  }
}
